// components/tenantPaymentSettings/TenantPaymentSettingsForm.tsx
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  CreateTenantPaymentSettingsDto,
  UpdateTenantPaymentSettingsDto,
  ResponseTenantPaymentSettingsDto
} from '../../types/tenantPaymentSettings.types';
import {
  createTenantPaymentSettings,
  updateTenantPaymentSettings,
  getTenantPaymentSettingsById
} from '../../service/tenantPaymentSettings/TenantPaymentSettingsService';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';

interface TenantPaymentSettingsFormProps {
  id?: number;
  onSuccess: () => void;
  onCancel: () => void;
}

export function TenantPaymentSettingsForm({ id, onSuccess, onCancel }: TenantPaymentSettingsFormProps) {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<CreateTenantPaymentSettingsDto | UpdateTenantPaymentSettingsDto>({
    tenant_id: localStorage.getItem('tenant_id') ? parseInt(localStorage.getItem('tenant_id') || '0') : 0,
    secret_key: '',
    webhook_secret: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSecretKeyChanged, setIsSecretKeyChanged] = useState(false);
  const [isWebhookSecretChanged, setIsWebhookSecretChanged] = useState(false);

  useEffect(() => {
    if (id) {
      loadPaymentSetting(id);
    }
  }, [id]);

  const loadPaymentSetting = async (id: number) => {
    const result = await getTenantPaymentSettingsById(dispatch, id);
    if (result) {
      setFormData({
        tenant_id: result.tenant_id,
        secret_key: "",
        webhook_secret: "",
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'tenant_id' ? parseInt(value) || 0 : value,
    }));
    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.secret_key || formData.secret_key.trim() === '') {
      newErrors.secret_key = 'Secret Key is required';
    }

    if (!formData.webhook_secret || formData.webhook_secret.trim() === '') {
      newErrors.webhook_secret = 'Webhook Secret is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (id) {
        // Update existing payment setting
        await updateTenantPaymentSettings(dispatch, id, formData as UpdateTenantPaymentSettingsDto);
      } else {
        // Create new payment setting
        await createTenantPaymentSettings(dispatch, formData as CreateTenantPaymentSettingsDto);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving payment settings:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className='invoiceCard p-0 card_content_padding card_custonized' title={id ? 'Edit Payment Setting' : 'Create Payment Setting'}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* HIGHLIGHT START */}
        <Input
          label="Secret Key"
          name="secret_key"
          type="text"
          value={
            isSecretKeyChanged ? formData.secret_key : '****'
          }
          onChange={handleChange}
          onFocus={() => {
            if (!isSecretKeyChanged) {
              setIsSecretKeyChanged(true);
              setFormData(prev => ({ ...prev, secret_key: '' }));
            }
          }}
          onBlur={() => {
            if (isSecretKeyChanged && !formData.secret_key) {
              setIsSecretKeyChanged(false);
            }
          }}
          error={errors.secret_key}
          required
        />

        <Input
          label="Webhook Secret"
          name="webhook_secret"
          type="text"
          value={
            isWebhookSecretChanged ? formData.webhook_secret : '****'
          }
          onFocus={() => {
            if (!isWebhookSecretChanged) {
              setIsWebhookSecretChanged(true);
              setFormData(prev => ({ ...prev, webhook_secret: '' }));
            }
          }}
          onBlur={() => {
            if (isWebhookSecretChanged && !formData.webhook_secret) {
              setIsWebhookSecretChanged(false);
            }
          }}
          onChange={handleChange}
          error={errors.webhook_secret}
          required
        />
        {/* HIGHLIGHT END */}

        <div className="flex justify-end space-x-4">
          <Button
            className="actionBtn bg-transparent hover:bg-gold hover:text-white border-gold text-gold"
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
          >
            {id ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Card>
  );
}